import { useEffect, useRef, useState } from "react";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { Input } from "../../../components/ui/Input";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";

const DEBOUNCE_MS = 500;

export function SearchInput({
  value: externalValue,
  placeholder,
  autoFocus = false,
  onSearch,
}: {
  value: string;
  placeholder: string;
  autoFocus?: boolean;
  onSearch: (value: string, source: "typed") => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [value, setValue] = useState(externalValue);
  const debounced = useDebouncedValue(value, DEBOUNCE_MS);

  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  const externalValueRef = useRef(externalValue);
  externalValueRef.current = externalValue;

  useEffect(() => {
    setValue((current) =>
      current === externalValue ? current : externalValue
    );
  }, [externalValue]);

  useEffect(() => {
    const trimmed = debounced.trim();
    if (trimmed === externalValueRef.current) return;
    onSearchRef.current(trimmed, "typed");
  }, [debounced]);

  return (
    <Input
      value={value}
      placeholder={placeholder}
      autoFocus={autoFocus}
      onChange={(e) => {
        setValue(e.target.value);
      }}
      trailing={
        value.length === 0 ? (
          <IconSearch className="size-5 text-ds-slate-400" />
        ) : (
          <button
            type="button"
            aria-label={t("Clear search")}
            className="flex size-8 items-center justify-center rounded-full text-ds-slate-400 hover:text-ds-black"
            onClick={() => {
              setValue("");
            }}
          >
            <IconX className="size-4" />
          </button>
        )
      }
    />
  );
}
